import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import AuthPage from './pages/AuthPage.jsx';
import RecruiterDashboard from './pages/RecruiterDashboard.jsx';
import CandidateInterview from './pages/CandidateInterview.jsx';

// Shows spinner while auth state is being restored from localStorage
function AuthGate({ children }) {
  const { loading } = useAuth();
  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#060e1a',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        border: '3px solid rgba(79,209,255,0.15)', borderTopColor: '#4fd1ff',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
  return children;
}

// Redirects to /login if not authenticated
function PrivateRoute({ children }) {
  const { recruiter } = useAuth();
  return recruiter ? children : <Navigate to="/login" replace />;
}

// Redirects to /recruiter if already logged in
function PublicRoute({ children }) {
  const { recruiter } = useAuth();
  return recruiter ? <Navigate to="/recruiter" replace /> : children;
}

function NotFound() {
  const nav = useNavigate();
  const { recruiter } = useAuth();
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#060e1a', color: '#e8f0fe',
    }}>
      <div style={{
        background: 'rgba(10,20,38,0.92)', border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 20, padding: '48px 32px', maxWidth: 420, textAlign: 'center',
      }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔍</div>
        <h2 style={{ marginBottom: 8, fontSize: 20, fontWeight: 800 }}>Page Not Found</h2>
        <p style={{ color: '#6b8aad', marginBottom: 24, fontSize: 14 }}>The page you're looking for doesn't exist.</p>
        <button
          onClick={() => nav(recruiter ? '/recruiter' : '/login')}
          style={{
            padding: '10px 24px', borderRadius: 999, border: 'none',
            background: 'linear-gradient(135deg,#4fd1ff,#6b7cff)',
            color: '#03101f', fontWeight: 700, cursor: 'pointer', fontSize: 14,
          }}
        >
          {recruiter ? 'Go to Dashboard' : 'Go to Login'}
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthGate>
        <Routes>
          <Route path="/" element={<Navigate to="/recruiter" replace />} />

          {/* Public — login/register */}
          <Route path="/login" element={
            <PublicRoute><AuthPage /></PublicRoute>
          } />

          {/* Protected — recruiter dashboard */}
          <Route path="/recruiter" element={
            <PrivateRoute><RecruiterDashboard /></PrivateRoute>
          } />

          {/* Public — candidate interview link (no auth needed) */}
          <Route path="/interview/:token" element={<CandidateInterview />} />

          {/* Candidate Dashboard Alias Fallbacks */}
          <Route path="/dashboard/candidate" element={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#060e1a', color: '#e8f0fe' }}>
              <div style={{ background: 'rgba(10,20,38,0.92)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, padding: '44px 32px', maxWidth: 460, textAlign: 'center' }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>🎯</div>
                <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800 }}>Candidate Interview Portal</h2>
                <p style={{ color: '#6b8aad', lineHeight: 1.7, fontSize: 14, marginBottom: 20 }}>
                  Please use the unique interview link provided to you by your recruiter to start your session.
                </p>
              </div>
            </div>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthGate>
    </BrowserRouter>
  );
}
